import { useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { resizeImageIfNeeded } from "@/lib/imageResize";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { fmtCAD, todayISO } from "@/lib/format";
import { Plus, Pill } from "@phosphor-icons/react";
import { PrescriptionsServicesFields, ReceiptDropzone, CRA_CATEGORIES } from "@/components/forms/TripFormDialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

function n(x) { return Number(x) || 0; }

export default function PrescriptionFormDialog({ initial, trigger }) {
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();
  const fileRef = useRef(null);

  const [form, setForm] = useState({
    date: initial?.date || todayISO(),
    category: initial?.category || "Prescription",
    service_kind: initial?.service_kind || "prescription",
    drug_name: initial?.drug_name || "",
    drug_pharmacy: initial?.drug_pharmacy || "",
    din: initial?.din || "",
    service_total: initial?.service_total || "",
    service_covered: initial?.service_covered || "",
    drug_amount: initial?.drug_amount || "",
    notes: initial?.notes || "",
    receipt_file_ids: initial?.receipt_file_ids || [],
    _receipt_meta: (initial?.receipt_file_ids || []).map((id) => ({ file_id: id, original_filename: "Receipt" })),
  });

  const stTotal = n(form.service_total);
  const stCovered = n(form.service_covered);
  const outOfPocket = stTotal > 0 ? Math.max(0, stTotal - stCovered) : n(form.drug_amount);

  const applyScan = (s) => {
    if (!s) return;
    setForm((f) => {
      const next = { ...f };
      if (s.date) next.date = s.date;
      if (s.kind === "prescription" || s.kind === "service") {
        next.service_kind = s.kind;
      }
      if (s.cra_category) next.category = s.cra_category;
      if (s.service_or_medication) next.drug_name = s.service_or_medication;
      if (s.vendor) next.drug_pharmacy = s.vendor;
      if (s.din) next.din = s.din;
      if (s.total != null) next.service_total = String(Number(s.total).toFixed(2));
      if (s.covered_by_insurance != null) next.service_covered = String(Number(s.covered_by_insurance).toFixed(2));
      if (s.out_of_pocket != null) next.drug_amount = String(Number(s.out_of_pocket).toFixed(2));
      return next;
    });
    if (s.cra_eligibility === "ineligible") {
      toast.warning("This expense may not be CRA-eligible", {
        description: s.cra_eligibility_reason || "Common ineligible items: gym memberships, OTC vitamins, cosmetic procedures. Confirm with your accountant before claiming.",
        duration: 9000,
      });
    } else {
      toast.success("Receipt scanned — fields auto-filled");
    }
  };

  const upload = useMutation({
    mutationFn: async (rawFile) => {
      const file = await resizeImageIfNeeded(rawFile);
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      let scanData = null;
      try {
        const r = await api.post(`/receipts/scan?file_id=${data.file_id}`);
        scanData = r.data;
      } catch { /* ignore */ }
      return { ...data, scan: scanData };
    },
    onSuccess: ({ file_id, original_filename, scan }) => {
      setForm((f) => ({
        ...f,
        receipt_file_ids: [...f.receipt_file_ids, file_id],
        _receipt_meta: [...f._receipt_meta, { file_id, original_filename }],
      }));
      toast.success("Receipt uploaded");
      if (scan) applyScan(scan);
    },
    onError: () => toast.error("Upload failed"),
  });

  const removeReceipt = (id) => setForm((f) => ({
    ...f,
    receipt_file_ids: f.receipt_file_ids.filter((x) => x !== id),
    _receipt_meta: f._receipt_meta.filter((x) => x.file_id !== id),
  }));

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        entry_type: "prescription",
        date: form.date,
        category: form.category || "Prescription",
        service_kind: form.service_kind,
        drug_name: form.drug_name,
        drug_pharmacy: form.drug_pharmacy,
        din: form.din,
        service_total: n(form.service_total),
        service_covered: n(form.service_covered),
        drug_amount: n(form.drug_amount),
        notes: form.notes,
        receipt_file_ids: form.receipt_file_ids,
        one_way_km: 0, round_trip: false,
        parking: 0, tolls: 0, meals: 0, accommodation: 0,
        gas: 0, taxi: 0, rental: 0, other_amount: 0, other_desc: "",
        destination: "", provider: "", purpose: "",
      };
      if (initial?.id) return (await api.patch(`/trips/${initial.id}`, body)).data;
      return (await api.post("/trips", body)).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["trips"] });
      qc.invalidateQueries({ queryKey: ["summary"] });
      toast.success(initial?.id ? "Updated" : "Saved");
      setOpen(false);
    },
    onError: () => toast.error("Could not save"),
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button data-testid="add-prescription-trigger" variant="outline" className="rounded-full border-[#E3E1D9] h-11">
            <Pill weight="duotone" size={18} className="mr-2" /> Rx / Service only
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg max-h-[90vh] flex flex-col p-0 rounded-2xl overflow-hidden">
        <DialogHeader className="px-6 pt-6 pb-4 shrink-0 border-b border-[#E3E1D9]">
          <DialogTitle>{initial?.id ? "Edit entry" : "New prescription or service"}</DialogTitle>
          <p className="text-sm text-[#5C6660] mt-1">Log out-of-pocket receipt costs here. If you traveled 40+ km one way for this service, don&apos;t forget to also log your journey as a &ldquo;Medical Trip&rdquo; on the dashboard.</p>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          <ReceiptDropzone
            fileRef={fileRef}
            uploading={upload.isPending}
            onPick={(f) => upload.mutate(f)}
            files={form._receipt_meta}
            onRemove={removeReceipt}
            accent="rx"
          />

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="mb-1.5 block">Date</Label>
              <Input data-testid="rx-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            </div>
            <div>
              <Label className="mb-1.5 block">CRA category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="rx-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CRA_CATEGORIES.map((c) => (
                    <SelectItem key={c.v} value={c.v}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <PrescriptionsServicesFields form={form} setForm={setForm} outOfPocket={outOfPocket} />

          <div>
            <Label className="mb-1.5 block">Notes</Label>
            <Textarea data-testid="rx-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="e.g. 90-day supply" />
          </div>

          <div className="rounded-2xl bg-[#1E2420] text-white p-4 flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/70">Out of pocket</div>
              <div className="font-[Manrope] text-2xl font-bold mt-0.5" data-testid="rx-total">{fmtCAD(outOfPocket)}</div>
            </div>
            <Pill weight="duotone" size={28} className="text-[#D4A373]" />
          </div>
        </div>

        <DialogFooter className="px-6 py-4 border-t border-[#E3E1D9] shrink-0 bg-white sm:rounded-b-2xl flex-row gap-2 sm:gap-3">
          <Button data-testid="rx-cancel" variant="outline" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>
          <Button
            data-testid="rx-save"
            disabled={!form.date || !form.drug_name || (!form.service_total && !form.drug_amount) || save.isPending}
            onClick={() => save.mutate()}
            className="rounded-full bg-[#2C4C3B] hover:bg-[#3A634D] text-white font-semibold px-6 shadow-md shadow-[#2C4C3B]/20 flex-1 sm:flex-none"
          >
            {save.isPending ? "Saving…" : (initial?.id ? "Save changes" : "Save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
